import { AxiosError } from 'axios';
import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';

import {
  createReferenceContact,
  createReferenceCheck,
  deleteReference,
  editReference,
  requestReference,
} from './referenceQueries';
import {
  CreateReferenceContactMutationVariables,
  CreateReferenceMutationVariables,
  CreateRequestMutationVariables,
  DeleteReferenceMutationVariables,
  EditReferenceMutationVariables,
  ReferenceRequestPayload,
} from '../types/referenceTypes';
import { HiringRequestListAPIData } from '../../Connect/features/ConnectATSIntegration/types';
import { hiringRequestQueries } from 'queryKeys/hiringRequest';
import { Reference as ReferenceResponse, ReferenceCheck } from 'types';
import { referenceQueries } from 'queryKeys/references';

export function useCreateReferenceRequest(
  candidateId: number,
  options?: Omit<
    UseMutationOptions<
      CreateRequestMutationVariables,
      AxiosError,
      ReferenceRequestPayload,
      unknown
    >,
    'mutationFn'
  >
): UseMutationResult<CreateRequestMutationVariables, AxiosError, ReferenceRequestPayload, unknown> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => requestReference(candidateId, payload),
    ...options,
    onSuccess: (data, variables, context) => {
      const queryKey = referenceQueries.references.pending(candidateId).queryKey;

      queryClient.invalidateQueries({
        queryKey,
        refetchType: 'all',
        exact: true,
      });

      options.onSuccess?.(data, variables, context);
    },
  });
}

export function useCreateReference(
  candidateId: number,
  districtId: number,
  options?: Omit<
    UseMutationOptions<ReferenceCheck, AxiosError, CreateReferenceMutationVariables, unknown>,
    'mutationFn'
  >
): UseMutationResult<ReferenceCheck, AxiosError, CreateReferenceMutationVariables, unknown> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ payload }) => createReferenceCheck(payload),
    ...options,
    onSuccess: (data, variables, context) => {
      const queryKey = referenceQueries.references.completedAndDraft(candidateId).queryKey;

      queryClient.invalidateQueries({
        queryKey,
        refetchType: 'all',
        exact: true,
      });

      const currentHiringRequestListData: HiringRequestListAPIData = queryClient.getQueryData(
        hiringRequestQueries.hiringRequests.list(districtId).queryKey
      );

      if (currentHiringRequestListData?.results) {
        const updatedResults = currentHiringRequestListData.results.map((hiringRequest) => {
          if (
            hiringRequest.candidate.id === candidateId &&
            !hiringRequest.has_references &&
            data.is_complete
          ) {
            return {
              ...hiringRequest,
              has_references: true,
            };
          }
          return hiringRequest;
        });

        queryClient.setQueryData(hiringRequestQueries.hiringRequests.list(districtId).queryKey, {
          ...currentHiringRequestListData,
          results: updatedResults,
        });
      }
      options.onSuccess?.(data, variables, context);
    },
  });
}

export function useCreateReferenceContact(
  candidateId: number,
  options?: Omit<
    UseMutationOptions<
      ReferenceResponse,
      AxiosError,
      CreateReferenceContactMutationVariables,
      unknown
    >,
    'mutationFn'
  >
): UseMutationResult<
  ReferenceResponse,
  AxiosError,
  CreateReferenceContactMutationVariables,
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ payload }) => createReferenceContact(payload),
    onSuccess: (data, variables, context) => {
      const currentQueryData: ReferenceResponse[] = queryClient.getQueryData(
        referenceQueries.references.contacts(candidateId).queryKey
      );

      const updatedData = currentQueryData.concat(data);

      queryClient.setQueryData(
        referenceQueries.references.contacts(candidateId).queryKey,
        updatedData
      );

      options.onSuccess?.(data, variables, context);
    },
  });
}

export function useEditReference(
  candidateId: number,
  districtId: number,
  referenceId: number,
  options?: Omit<
    UseMutationOptions<ReferenceCheck, AxiosError, EditReferenceMutationVariables, unknown>,
    'mutationFn'
  >
): UseMutationResult<ReferenceCheck, AxiosError, EditReferenceMutationVariables, unknown> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ payload }) => {
      return editReference(referenceId, payload);
    },
    ...options,
    onSuccess: (data, variables, context) => {
      const queryKey = referenceQueries.references.completedAndDraft(candidateId).queryKey;

      queryClient.invalidateQueries({
        queryKey,
        refetchType: 'all',
        exact: true,
      });

      const currentHiringRequestListData: HiringRequestListAPIData = queryClient.getQueryData(
        hiringRequestQueries.hiringRequests.list(districtId).queryKey
      );

      if (currentHiringRequestListData?.results) {
        const updatedResults = currentHiringRequestListData.results.map((hiringRequest) => {
          if (
            hiringRequest.candidate.id === candidateId &&
            !hiringRequest.has_references &&
            data.is_complete
          ) {
            return {
              ...hiringRequest,
              has_references: true,
            };
          }
          return hiringRequest;
        });

        queryClient.setQueryData(hiringRequestQueries.hiringRequests.list(districtId).queryKey, {
          ...currentHiringRequestListData,
          results: updatedResults,
        });
      }

      options.onSuccess?.(data, variables, context);
    },
  });
}

export function useDeleteReference(
  districtId: number,
  options?: Omit<
    UseMutationOptions<'', unknown, DeleteReferenceMutationVariables, unknown>,
    'mutationFn'
  >
): UseMutationResult<'', unknown, DeleteReferenceMutationVariables, unknown> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ referenceId }) => {
      const response = await deleteReference(referenceId);
      return response;
    },
    ...options,
    onSuccess: (data, variables, context) => {
      options.onSuccess?.(data, variables, context);

      const currentQueryData: ReferenceCheck[] = queryClient.getQueryData(
        referenceQueries.references.completedAndDraft(variables.candidateId).queryKey
      );

      const updatedData = currentQueryData.filter((datum) => datum.id !== variables.referenceId);

      queryClient.setQueryData(
        referenceQueries.references.completedAndDraft(variables.candidateId).queryKey,
        updatedData
      );

      const completedReferences = updatedData.filter((datum) => datum.is_complete);
      const hiringRequestsQueryKey = hiringRequestQueries.hiringRequests.list(districtId).queryKey;
      const currentHiringRequestListData: HiringRequestListAPIData =
        queryClient.getQueryData(hiringRequestsQueryKey);

      if (completedReferences.length === 0) {
        const updatedResults = currentHiringRequestListData.results?.map((hiringRequest) => {
          if (hiringRequest.candidate.id === variables.candidateId) {
            return {
              ...hiringRequest,
              has_references: false,
            };
          }
          return hiringRequest;
        });

        queryClient.setQueryData(hiringRequestsQueryKey, {
          ...currentHiringRequestListData,
          results: updatedResults,
        });
      }
    },
  });
}
